import React, { useEffect, useRef, useCallback } from "react";
import { HEIGHT, SHOW_HEIGHT, SHOW_WIDTH, WIDTH } from "../hooks/useCanvas";

export const CANVAS_MODE = {
  TEACH: 0,
  PREDICT: 1,
};

const Canvas = ({
  mainCanvasRef,
  hiddenCanvasRef,
  startDrawing,
  draw,
  stopDrawing,
  config,
  mode = CANVAS_MODE.TEACH,
}) => {
  const isDrawing = useRef(false);
  const lastPoint = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef(null);
  const { ENLARGE_RATIO, WIDTH, HEIGHT } = config;

  const getCoordinates = useCallback(
    (event) => {
      const rect = mainCanvasRef.current.getBoundingClientRect();
      if (event.touches && event.touches.length > 0) {
        return {
          x: event.touches[0].clientX - rect.left,
          y: event.touches[0].clientY - rect.top,
        };
      } else {
        return {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
      }
    },
    [mainCanvasRef]
  );

  const handleTouchStart = useCallback(
    (e) => {
      e.preventDefault();
      isDrawing.current = true;
      const touch = e.touches[0];
      const rect = mainCanvasRef.current.getBoundingClientRect();
      lastPoint.current = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
      startDrawing(e);
    },
    [startDrawing, mainCanvasRef]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      const rect = mainCanvasRef.current.getBoundingClientRect();
      const newPoint = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
      draw(newPoint);
      lastPoint.current = newPoint;
    },
    [draw, mainCanvasRef]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      isDrawing.current = false;
      stopDrawing(e);
    },
    [stopDrawing]
  );

  const handleMouseDown = useCallback(
    (e) => {
      isDrawing.current = true;
      const point = getCoordinates(e);
      lastPoint.current = point;
      startDrawing(e);
    },
    [startDrawing, getCoordinates]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDrawing.current) return;
      const point = getCoordinates(e);
      draw(point);
      lastPoint.current = point;
    },
    [draw, getCoordinates]
  );

  const handleMouseUp = useCallback(
    (e) => {
      isDrawing.current = false;
      stopDrawing(e);
    },
    [stopDrawing]
  );

  const handleMouseOut = useCallback(
    (e) => {
      if (isDrawing.current) {
        handleMouseUp(e);
      }
    },
    [handleMouseUp]
  );

  useEffect(() => {
    const canvas = mainCanvasRef.current;

    canvas.addEventListener("touchstart", handleTouchStart);
    canvas.addEventListener("touchmove", handleTouchMove);
    canvas.addEventListener("touchend", handleTouchEnd);
    canvas.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <>
      <canvas
        ref={mainCanvasRef}
        width={WIDTH * ENLARGE_RATIO}
        height={HEIGHT * ENLARGE_RATIO}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseOut={handleMouseOut}
        style={{ touchAction: "none", border: "1px solid black" }}
        className="border rounded-md mb-4"
      />
      <canvas
        ref={hiddenCanvasRef}
        width={mode == CANVAS_MODE.TEACH ? WIDTH * ENLARGE_RATIO : WIDTH}
        height={mode == CANVAS_MODE.TEACH ? HEIGHT * ENLARGE_RATIO : HEIGHT}
        style={{ display: "none", border: "1px solid black" }}
      />
    </>
  );
};

export default Canvas;
